import { useState, useEffect } from "react";
import { Shield, BarChart3 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ModalCard } from "@/components/flowly/infra/ModalCard/ModalCard";

interface CookiePreferenceCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (analytics: boolean) => void;
  currentAnalytics: boolean;
}

export function CookiePreferenceCenter({
  isOpen,
  onClose,
  onSave,
  currentAnalytics,
}: CookiePreferenceCenterProps) {
  const { t } = useLanguage();
  const [analytics, setAnalytics] = useState(currentAnalytics);

  // Sync local state when modal opens with current preferences
  useEffect(() => {
    if (isOpen) {
      setAnalytics(currentAnalytics);
    }
  }, [isOpen, currentAnalytics]);

  return (
    <ModalCard isOpen={isOpen} onClose={onClose} maxWidth="max-w-lg">
      <div className="text-start">
        <h2 className="text-lg font-semibold text-foreground mb-2">
          {t.cookieConsent.preferenceCenterTitle}
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          {t.cookieConsent.preferenceCenterDescription}
        </p>

        <div className="space-y-4">
          {/* Essential Cookies */}
          <div className="flex items-start gap-4 p-4 rounded-xl bg-secondary/50">
            <Shield className="w-5 h-5 text-foreground mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-foreground">
                  {t.cookieConsent.essentialTitle}
                </span>
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  {t.cookieConsent.essentialBadge}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t.cookieConsent.essentialDescription}
              </p>
            </div>
            <Switch checked disabled className="shrink-0" />
          </div>

          {/* Analytics Cookies */}
          <div className="flex items-start gap-4 p-4 rounded-xl bg-secondary/50">
            <BarChart3 className="w-5 h-5 text-foreground mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-foreground block mb-1">
                {t.cookieConsent.analyticsTitle}
              </span>
              <p className="text-xs text-muted-foreground">
                {t.cookieConsent.analyticsDescription}
              </p>
            </div>
            <Switch
              checked={analytics}
              onCheckedChange={setAnalytics}
              className="shrink-0"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6 justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t.cookieConsent.cancel}
          </Button>
          <Button size="sm" onClick={() => onSave(analytics)}>
            {t.cookieConsent.savePreferences}
          </Button>
        </div>
      </div>
    </ModalCard>
  );
}
